import React, { ReactNode, useEffect, useRef, useState, VideoHTMLAttributes } from 'react';
import { connect } from 'react-redux';
import { IParticipantForUser, StreamType, TUserId } from '../../PartyClientResponse';
import { xForAngle, yForAngle } from '../../components/Utils';
import { AppActions, AppMapProps, IAppState, IReduxActions } from '../../redux/reducer';
import { useSubscription } from './VideoControlContext';
import { useAppState } from '../../state/AppState';
import { gql, useMutation } from '@apollo/client';
import { Spin } from 'antd';
import { highlightParticipant } from '../client/PartyClient';
import { getIdToken } from '../PartyDB';

const styleForSize = (width: number, height: number): React.CSSProperties => ({
  width,
  height,
  // maxWidth: width,
  // maxHeight: height,
});

const STEP_DOWN_AS_MAIN = gql`
  mutation StepDownAsMain($partyKey: String!, $participantId: String) {
    updateMainParticipantStream(input: { partyKey: $partyKey, participantId: $participantId }) {
      success
    }
  }
`;

const useSpotlightState = (participant: IParticipantForUser) => {
  const { user, party, isHost, isMod } = useAppState();
  const [highlightLoading, setLoading] = useState<boolean>(false);
  const [stepDown, { loading: stepDownLoading }] = useMutation(STEP_DOWN_AS_MAIN);

  const canUnspotLight = isHost || isMod || user?.uid === participant.uid;
  const isSpotlighted = party?.stage.spotlight?.participant.uid === participant.uid;
  const isMainVideo =
    party?.stage?.stream.type === StreamType.Participant &&
    party?.stage?.stream.mainParticipantId === participant.uid;

  const loading = highlightLoading || stepDownLoading;
  let text = '';
  let onClick;
  if (isMainVideo && canUnspotLight) {
    text = isHost ? 'unfocus' : 'step down';
    onClick = async () => {
      if (!party) {
        return;
      }
      try {
        await stepDown({
          variables: {
            partyKey: party?.key,
            participantId: null,
          },
        });
      } catch (err) {
        console.log(err);
      }
    };
  }

  if (isSpotlighted && canUnspotLight) {
    text = isHost ? 'unspotlight' : 'step down';
    onClick = async () => {
      if (!party) {
        return;
      }
      setLoading(true);
      try {
        await highlightParticipant({
          idToken: await getIdToken(),
          key: party?.key,
          participant: null,
        });
      } catch (err) {
        console.log(err);
      }
      setLoading(false);
    };
  }

  return {
    loading,
    text,
    onClick,
    shouldShow: (isMainVideo || isSpotlighted) && canUnspotLight,
  };
};

const StreamPlayer = ({
  children,
  height,
  audioEnabled,
  participant,
  width,
  debug,
  shouldSubscribe = true,
  square = false,
  className = '',
}: {
  children?: ReactNode;
  height: number;
  audioEnabled: boolean;
  participant: IParticipantForUser;
  width: number;
  shouldSubscribe?: boolean;
  className?: string;
  square?: boolean;
} & IAppState &
  IReduxActions) => {
  const [isAudioOutputBlocked, setAudioOutputBlocked] = useState<boolean>(false);
  const [isVideoOutputBlocked, setVideoOutputBlocked] = useState<boolean>(false);
  const { loading, onClick, text, shouldShow } = useSpotlightState(participant);
  const { party } = useAppState();
  const spotlightedParticipantId = party?.stage.spotlight?.participant?.uid;
  const isSpotlistedParticipant = participant.uid === spotlightedParticipantId;

  const isSourceMuted = !audioEnabled;

  const onToggleParticpantAudio = () => {
    setAudioOutputBlocked(!isAudioOutputBlocked);
  };

  const onToggleParticipantVideo = () => {
    setVideoOutputBlocked(!isVideoOutputBlocked);
  };

  const style = styleForSize(width, height);

  const angleForLocalAudio = 45;
  const angleForLocalVideo = 135;
  const angleForStepDown = 90;

  const muted = isAudioOutputBlocked || isSourceMuted;

  const { setVideoContainerRef, videoEnabled, audioLevel } = useSubscription({
    shouldSubscribe,
    participant,
    subscribeToAudio: !muted,
    subscribeToVideo: !isVideoOutputBlocked,
    options: { enableAudioLevel: true },
  });

  const boxShadow = audioEnabled
    ? `0 0 ${Math.floor(audioLevel * 100 + 20)}px ${Math.floor(audioLevel * 10)}px white`
    : 'none';

  return (
    <div
      key={`${participant.uid}`}
      className={'stream-player'}
      style={{ position: 'relative', width, height }}>
      <div
        className={`p-mic-indicator ${isSpotlistedParticipant ? 'spotlighted' : ''} ${
          square ? 'square' : ''
        }`}
        style={{
          position: 'absolute',
          width,
          height,
          willChange: 'box-shadow',
          boxShadow,
        }}></div>
      <div
        className={`ot-video-container ${className} ${square ? 'square' : ''}`}
        style={{ ...style, opacity: videoEnabled ? 1 : 0 }}
        ref={setVideoContainerRef}
      />
      {!!participant.fakeVideoUrl && !isVideoOutputBlocked && (
        <video
          // width={BW}
          autoPlay
          controls={false}
          loop
          // height={BH}
          className={`fake-video ${square ? 'square' : ''}`}
          // ref={refVideo}
          src={participant.fakeVideoUrl}
          muted
          style={{
            position: 'absolute',
            width,
            height,
          }}
          // style={{ display: 'none' }}
        />
      )}
      <div key={`${participant.uid}-volume`} style={style}>
        {children}
      </div>
      <>
        <div
          key='enable-audio'
          className={`stream-player-button audio ${isAudioOutputBlocked ? 'disabled' : 'enabled'}`}
          style={{
            transform: `translate(${xForAngle(width / 2, angleForLocalAudio)}px, ${yForAngle(
              width / 2,
              angleForLocalAudio
            )}px)`,
          }}
          onClick={onToggleParticpantAudio}>
          <img
            alt='Toggle audio'
            className='stream-player-icon'
            src={isAudioOutputBlocked ? '/icons/mic_off.svg' : '/icons/mic.svg'}
          />
        </div>
        {shouldShow && (
          <div
            key='step-down'
            className={`stream-player-button step-down`}
            style={{
              transform: `translate(${xForAngle(width / 2, angleForStepDown)}px, ${yForAngle(
                width / 2,
                angleForStepDown
              )}px)`,
            }}
            onClick={onClick}>
            {loading ? (
              <Spin />
            ) : (
              <img alt={text} className='stream-player-icon' src={'/icons/Close_window.svg'} />
            )}
          </div>
        )}
        <div
          key='enable-video'
          className={`stream-player-button video ${isVideoOutputBlocked ? 'enabled' : 'disabled'}`}
          style={{
            transform: `translate(${xForAngle(width / 2, angleForLocalVideo)}px, ${yForAngle(
              width / 2,
              angleForLocalVideo
            )}px)`,
          }}
          onClick={onToggleParticipantVideo}>
          <img
            className='stream-player-icon'
            alt='Toggle video'
            src={isVideoOutputBlocked ? '/icons/camera_off.svg' : '/icons/camera.svg'}
          />
        </div>
      </>
    </div>
  );
};

type VideoWithRefPropsType = VideoHTMLAttributes<HTMLVideoElement> & {
  id?: TUserId;
  key: string;
  videoTrack: MediaStreamTrack | null;
};
export const VideoWithRef = ({ id, videoTrack, style, ...props }: VideoWithRefPropsType) => {
  const [opacity, setOpacity] = useState(0);
  const refVideo = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!refVideo.current || !videoTrack) return;
    refVideo.current.srcObject = new MediaStream([videoTrack]);
  }, [videoTrack, refVideo]);

  const prevMutedStateCount = useRef<number>(0);
  useEffect(() => {
    const handle = setInterval(() => {
      if (videoTrack) {
        if (!videoTrack) {
          setOpacity(0);
          // } else if (videoTrack.muted && prevMutedStateCount.current > 4) {
          //   // This is a hack. For some reason 'muted' spends about 25% of its time
          //   // as muted even when it's not supposed to be, so let's poll and call it
          //   // muted if it stays that way for more than 4 seconds
          //   // Note: the muted property is how we determine is the use has disabled their video
          //   setOpacity(0);
        } else if (videoTrack.enabled && videoTrack?.readyState === 'live') {
          setOpacity(1);
        } else {
          setOpacity(0);
        }

        if (videoTrack?.muted) {
          prevMutedStateCount.current++;
        } else {
          prevMutedStateCount.current = 0;
        }
      }
    }, 1000);
    return () => clearInterval(handle);
  }, [videoTrack]);

  useEffect(() => {
    if (refVideo.current) {
      refVideo.current.play();
    }
  }, [refVideo]);

  return (
    <video
      className={'video'}
      ref={refVideo}
      {...props}
      style={{
        ...style,
        position: 'relative',
        objectFit: 'cover',
        opacity,
      }}
    />
  );
};

export default connect(AppMapProps, AppActions)(StreamPlayer);
